import { useCallback, useEffect, useMemo, useState } from "react";
import type { Identifier } from "ra-core";
import { useDataProvider, useGetList, useNotify } from "ra-core";

import type { Contact } from "../types";
import { applyRefinements, type SwipeRefinementSelection } from "./refinements";
import {
  useSwipeSuggestions,
  type SwipeSuggestion,
} from "./useSwipeSuggestions";
import {
  resolveFilter,
  type ContactPatch,
  type SwipeAction,
  type SwipeScenario,
} from "./types";

/**
 * How many cards to hold. Big enough that the queue never visibly stalls
 * between refetches, small enough that a phone is not parsing a thousand
 * contacts to show one card.
 */
const PAGE_SIZE = 40;

/** Refill once the buffer drops this low, so the fetch overlaps the swiping. */
const REFILL_AT = 12;

/** Undo depth. Deep enough to unwind a misfire, shallow enough to stay honest. */
const HISTORY_LIMIT = 20;

export interface SwipeDecision {
  contact: Contact;
  /** What was written. `null` for a skip, which writes nothing. */
  patch: ContactPatch | null;
  /** The same keys as they were before, so undo restores exactly what changed. */
  previous: ContactPatch;
  skipped: boolean;
}

export interface SwipeQueue {
  current: Contact | undefined;
  /** The first-pass recommendation for the current card, if it has one. */
  suggestion: SwipeSuggestion | undefined;
  /** The card rendered behind the current one, so the stack has depth. */
  next: Contact | undefined;
  isPending: boolean;
  error: unknown;
  /** Contacts still to answer in this phase, as far as the server knows. */
  remaining: number | undefined;
  /** True once the queue is empty and there is nothing left to recycle. */
  isExhausted: boolean;
  /** True while serving the recycle phase rather than the open queue. */
  isRecycling: boolean;
  /** True while serving scored recommendations ahead of the unranked queue. */
  isRanked: boolean;
  /** Scored contacts still to come, including the current card. */
  rankedRemaining: number;
  answered: number;
  skipped: number;
  streak: number;
  canUndo: boolean;
  answer: (action: SwipeAction) => void;
  skip: () => void;
  undo: () => void;
}

/**
 * The value a field held before a patch touched it, in a form that can be
 * written back. A missing field has to become `null`: `undefined` is dropped
 * on its way to PostgREST, so undoing "set icp_fit" on a contact that had none
 * would silently leave the new value in place.
 */
const snapshot = (contact: Contact, patch: ContactPatch): ContactPatch => {
  const previous: ContactPatch = {};
  for (const key of Object.keys(patch)) {
    const value = (contact as unknown as Record<string, unknown>)[key];
    previous[key] = value == null ? null : (value as string | number);
  }
  return previous;
};

/**
 * The queue behind a swipe scenario: what to show, what a decision writes, and
 * how to take one back.
 *
 * Scenario-agnostic on purpose — it knows about filters, phases and patches,
 * never about ICP or motions. Writes are optimistic: the card leaves the moment
 * you answer, and a failed write puts it back and says why. Anything else makes
 * the interface feel like a form, which is the thing it is trying not to be.
 */
export function useSwipeQueue(
  scenario: SwipeScenario,
  selection: SwipeRefinementSelection,
  /** Pinned by the caller for the life of the mount; see SwipeFilterSpec. */
  now: Date,
): SwipeQueue {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [isRecycling, setIsRecycling] = useState(false);
  /** Ids taken out of the local buffer, so a refetch cannot serve them twice. */
  const [consumed, setConsumed] = useState<ReadonlySet<Identifier>>(
    () => new Set(),
  );
  /** Cards put back by an undo, served ahead of the buffer. */
  const [restored, setRestored] = useState<readonly Contact[]>([]);
  const [history, setHistory] = useState<readonly SwipeDecision[]>([]);
  const [answered, setAnswered] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [streak, setStreak] = useState(0);

  const phase = isRecycling && scenario.recycle ? scenario.recycle : scenario;

  // The first-pass ranking, if this scenario has had one. Ranked contacts are
  // served ahead of the ordinary queue: they are the whole reason the deck is
  // worth opening when the unranked queue is twelve thousand cards long.
  const suggestions = useSwipeSuggestions(scenario.id);

  // Both the query and the reset below key off a serialisation rather than the
  // selection's identity. A caller that rebuilt the object each render would
  // otherwise change the query key each render, which is a refetch storm
  // against Supabase rather than a cosmetic bug.
  const selectionKey = JSON.stringify(selection);
  const filter = useMemo(
    () =>
      applyRefinements(
        resolveFilter(phase.filter, now),
        scenario,
        selection,
        now,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phase.filter, scenario, selectionKey, now],
  );

  const { data, total, isPending, error, refetch } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: PAGE_SIZE },
      sort: phase.sort,
      filter,
    },
  );

  // Ranked contacts are fetched through the scenario's own filter too, so one
  // that has been answered since the pass ran simply drops out instead of
  // coming back with a stale recommendation.
  const rankedIds = suggestions.orderedIds;
  const { data: rankedData, isPending: rankedPending } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: rankedIds.length || 1 },
      sort: phase.sort,
      filter: { ...filter, "id@in": `(${rankedIds.join(",")})` },
    },
    { enabled: rankedIds.length > 0 && !isRecycling },
  );

  // Server order is the scenario's sort; re-impose the ranking, which is the
  // only order that matters here.
  const ranked = useMemo(() => {
    if (rankedIds.length === 0) return [];
    const available = new Map((rankedData ?? []).map((c) => [c.id, c]));
    return rankedIds
      .map((id) => available.get(id))
      .filter((c): c is Contact => Boolean(c))
      .filter((c) => !consumed.has(c.id));
  }, [rankedIds, rankedData, consumed]);

  const buffer = useMemo(() => {
    // A card restored by undo is usually still in the server page it came
    // from, so dedupe by id — otherwise undo hands you the same person twice.
    const seen = new Set<Identifier>();
    const out: Contact[] = [];
    for (const contact of [
      ...restored,
      ...ranked,
      ...(data ?? []).filter((c) => !consumed.has(c.id)),
    ]) {
      if (seen.has(contact.id)) continue;
      seen.add(contact.id);
      out.push(contact);
    }
    return out;
  }, [data, consumed, restored, ranked]);

  // Refill once the buffer runs low, so the fetch overlaps the swiping rather
  // than interrupting it. react-query dedupes concurrent refetches.
  const needsRefill =
    !isPending && !error && buffer.length < REFILL_AT && (total ?? 0) > 0;
  useEffect(() => {
    if (needsRefill) void refetch();
  }, [needsRefill, refetch]);

  // Nothing left in this phase: fall through to the recycle phase if the
  // scenario has one. Guarded on `total` so an empty local buffer mid-refetch
  // does not trip it.
  const shouldRecycle =
    !isPending && !isRecycling && scenario.recycle != null && total === 0;
  useEffect(() => {
    if (shouldRecycle) setIsRecycling(true);
  }, [shouldRecycle]);

  // Changing the refinements is a different queue, not a continuation of this
  // one: everything already consumed belongs to the old filter.
  useEffect(() => {
    setConsumed(new Set());
    setRestored([]);
    setHistory([]);
    setStreak(0);
  }, [selectionKey, scenario.id]);

  const consume = useCallback((contact: Contact) => {
    setRestored((prev) =>
      prev.length && prev[0].id === contact.id ? prev.slice(1) : prev,
    );
    setConsumed((prev) => {
      const next = new Set(prev);
      next.add(contact.id);
      return next;
    });
  }, []);

  const pushHistory = useCallback((decision: SwipeDecision) => {
    setHistory((prev) => [decision, ...prev].slice(0, HISTORY_LIMIT));
  }, []);

  const current = buffer[0];
  const next = buffer[1];

  const write = useCallback(
    async (contact: Contact, patch: ContactPatch, previous: Contact) => {
      try {
        await dataProvider.update("contacts", {
          id: contact.id,
          data: patch,
          previousData: previous,
        });
      } catch (e) {
        // Put the card back rather than swallow it: an unwritten decision that
        // looks written is worse than an interruption.
        setConsumed((prev) => {
          const rest = new Set(prev);
          rest.delete(contact.id);
          return rest;
        });
        setRestored((prev) => [contact, ...prev]);
        setStreak(0);
        notify(
          typeof e === "object" && e && "message" in e
            ? String((e as { message: unknown }).message)
            : "Could not save that decision",
          { type: "error" },
        );
      }
    },
    [dataProvider, notify],
  );

  const answer = useCallback(
    (action: SwipeAction) => {
      if (!current) return;
      const contact = current;
      // `last_triaged_at` is stamped here rather than in every action: when a
      // decision was made is the queue's concern, not the scenario's.
      const at = new Date();
      const patch: ContactPatch = {
        ...action.patch(contact, at),
        last_triaged_at: at.toISOString(),
      };

      consume(contact);
      setAnswered((n) => n + 1);
      setStreak((n) => n + 1);
      pushHistory({
        contact,
        patch,
        previous: snapshot(contact, patch),
        skipped: false,
      });

      void write(contact, patch, contact);
    },
    [current, consume, pushHistory, write],
  );

  const skip = useCallback(() => {
    if (!current) return;
    const contact = current;
    consume(contact);
    setSkipped((n) => n + 1);
    setStreak(0);
    pushHistory({ contact, patch: null, previous: {}, skipped: true });
  }, [current, consume, pushHistory]);

  const undo = useCallback(() => {
    const [last, ...rest] = history;
    if (!last) return;
    setHistory(rest);
    setConsumed((prev) => {
      const nextSet = new Set(prev);
      nextSet.delete(last.contact.id);
      return nextSet;
    });
    setRestored((prev) => [last.contact, ...prev]);

    if (last.skipped) {
      setSkipped((n) => Math.max(0, n - 1));
      return;
    }

    setAnswered((n) => Math.max(0, n - 1));
    setStreak(0);
    void write(last.contact, last.previous, {
      ...last.contact,
      ...last.patch,
    } as Contact);
  }, [history, write]);

  return {
    current,
    next,
    suggestion: current ? suggestions.byContact.get(current.id) : undefined,
    isRanked: ranked.length > 0,
    rankedRemaining: ranked.length,
    isPending: (isPending || rankedPending) && buffer.length === 0,
    error,
    remaining: total ?? undefined,
    isExhausted: !isPending && buffer.length === 0 && (total ?? 0) === 0,
    isRecycling,
    answered,
    skipped,
    streak,
    canUndo: history.length > 0,
    answer,
    skip,
    undo,
  };
}
